// Core
import { useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import {
  AnchorNav,
  Button,
  Chip,
  Crumbs,
  FieldSelect,
  FieldText,
  PageHead,
  SectionCard,
} from "components/AtomsV2";
import { Icon } from "components/ShellV2";

// Redux
import { useDispatch, useSelector } from "react-redux";
import {
  openNotificationPopup,
  selectDensity,
  selectSidebarStyle,
  selectThemeMode,
  setDensity,
  setSidebarStyle,
  setThemeMode,
} from "state/application/applicationSlice";
import { selectCurrentUser, selectRole } from "state/auth/authSlice";
import { useResetPasswordRequestMutation } from "state/auth/authApiSlice";

// Utils
import { avColor, initials } from "utils/avatar";
import { getUserRoleLabelFromValue } from "utils/constants/helpers";
import IntegrationsSection from "./IntegrationsSection";

function UserSettings() {
  const dispatch = useDispatch();
  const { t } = useTranslation(["settings", "common"]);

  const user = useSelector(selectCurrentUser);
  const role = useSelector(selectRole);
  const themeMode = useSelector(selectThemeMode);
  const density = useSelector(selectDensity);
  const sidebarStyle = useSelector(selectSidebarStyle);

  const [resetPasswordRequest, { isLoading: isSending }] = useResetPasswordRequestMutation();
  const [sent, setSent] = useState(false);
  const onSendRecovery = async () => {
    try {
      await resetPasswordRequest(user.email).unwrap();
      setSent(true);
      dispatch(openNotificationPopup({ notification: t("password.sent"), type: "success" }));
    } catch {
      dispatch(openNotificationPopup({ notification: t("password.error"), type: "error" }));
    }
  };

  const navItems = [
    { id: "profile", icon: "person", label: t("profile.title") },
    { id: "prefs", icon: "tune", label: t("prefs.title") },
    { id: "password", icon: "lock", label: t("password.title") },
    { id: "integrations", icon: "hub", label: t("integrations.title") },
  ];

  return (
    <div>
      <Crumbs items={[{ label: t("title") }]} />
      <PageHead title={t("title")} subtitle={t("subtitle")} />

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] items-start gap-rec">
        <AnchorNav items={navItems} />

        <div className="flex flex-col gap-rec min-w-0">
          {/* hero */}
          <div className="rec-card rec-card-pad">
            <div className="rec-profile-hero">
              <div
                className="rec-av-lg"
                style={{ background: avColor(user?.name || user?.email || "") }}
              >
                {initials(user?.name || user?.email || "")}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-[10px] flex-wrap">
                  <h2 className="m-0 text-[21px] font-bold text-ink tracking-[-0.3px]">
                    {user?.name || "—"}
                  </h2>
                  <Chip tone="teal">{getUserRoleLabelFromValue(role)}</Chip>
                </div>
                <div className="mt-[6px] text-ink-3 text-[13.5px] inline-flex items-center gap-[6px]">
                  <Icon name="mail" style={{ fontSize: 16 }} />
                  {user?.email || "—"}
                </div>
              </div>
            </div>
          </div>

          {/* profile (read-only) */}
          <SectionCard id="profile" icon="person" title={t("profile.title")}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FieldText label={t("profile.name")} value={user?.name || ""} disabled />
              <FieldText label={t("profile.email")} value={user?.email || ""} disabled />
              <FieldText
                label={t("profile.role")}
                value={getUserRoleLabelFromValue(role)}
                disabled
              />
            </div>
          </SectionCard>

          {/* application preferences */}
          <SectionCard id="prefs" icon="tune" title={t("prefs.title")}>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <FieldSelect
                label={t("prefs.theme.label")}
                value={themeMode}
                options={[
                  { value: "light", label: t("prefs.theme.light") },
                  { value: "dark", label: t("prefs.theme.dark") },
                ]}
                onChange={(v) => dispatch(setThemeMode(v))}
              />
              <FieldSelect
                label={t("prefs.density.label")}
                value={density}
                options={[
                  { value: "dense", label: t("prefs.density.dense") },
                  { value: "balanced", label: t("prefs.density.balanced") },
                  { value: "spacious", label: t("prefs.density.spacious") },
                ]}
                onChange={(v) => dispatch(setDensity(v))}
              />
              <FieldSelect
                label={t("prefs.sidebar.label")}
                value={sidebarStyle}
                options={[
                  { value: "light", label: t("prefs.sidebar.light") },
                  { value: "branded", label: t("prefs.sidebar.branded") },
                  { value: "rail", label: t("prefs.sidebar.rail") },
                ]}
                onChange={(v) => dispatch(setSidebarStyle(v))}
              />
            </div>
          </SectionCard>

          {/* password */}
          <SectionCard
            id="password"
            icon="lock"
            title={t("password.title")}
            action={
              <Button
                variant="ghost"
                size="sm"
                onClick={onSendRecovery}
                disabled={isSending || sent}
              >
                <Icon name="forward_to_inbox" />
                {t("password.button")}
              </Button>
            }
          >
            <p className="text-[13px] text-ink-3 leading-[1.6] m-0 max-w-[560px]">
              {t("password.desc")}
            </p>
          </SectionCard>

          {/* external partner integrations */}
          <IntegrationsSection />
        </div>
      </div>
    </div>
  );
}

export default UserSettings;
